import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import { ArrowRight, Quote, BookOpen, Flame } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1531972111231-7482a960e109?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODE1NzM0NTB8MA&ixlib=rb-4.1.0&q=85";
const PORTRAIT = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBleGVjdXRpdmUlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODE1NzM0NTB8MA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { id: "strategy", label: "Strategy" },
  { id: "communication", label: "Communication" },
  { id: "team", label: "Team Building" },
  { id: "self", label: "Self-Mastery" },
];

export default function Home() {
  const [books, setBooks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [flagshipModules, setFlagshipModules] = useState(null);
  const [flagshipProduct, setFlagshipProduct] = useState(null);

  useEffect(() => {
    (async () => {
      const [b, c, bd, flag] = await Promise.all([
        api.get("/products", { params: { type: "book" } }),
        api.get("/products", { params: { type: "course" } }),
        api.get("/bundles"),
        api.get("/products/prd_b8dca9fc6cf1").catch(() => null),
      ]);
      setBooks(b.data.slice(0, 4));
      setCourses(c.data.slice(0, 3));
      setBundles(bd.data.slice(0, 3));
      if (flag?.data?.modules) setFlagshipModules(flag.data.modules);
      setFlagshipProduct(flag?.data || null);
    })();
  }, []);

  return (
    <Layout>
      <SEO
        title="Forged in Fire — Leadership Books & Courses by Robbie South"
        description="A deliberate library of leadership thinking for executives, founders, and senior operators. Home of Forged in Fire: The Course by Robbie South."
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden border-b border-brand-line">
        <div className="container-narrow grid lg:grid-cols-12 gap-10 py-20 md:py-28">
          <div className="lg:col-span-7 flex flex-col justify-center animate-fade-up">
            <span className="overline text-brand-rust mb-6">Est. 2026 — Leadership Library</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-brand-ink">
              Books and courses for leaders who <em className="italic">prefer signal</em> over noise.
            </h1>
            <p className="mt-7 text-base md:text-lg text-brand-inkMuted max-w-xl leading-relaxed">
              A small, deliberate library of leadership thinking — curated for executives, founders, and senior operators. No fluff, no theatrics.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/preview"
                data-testid="hero-free-preview"
                className="inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-black px-7 py-4 text-sm font-medium tracking-wide transition-colors"
              >
                Free preview — Module 1 <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/books"
                data-testid="hero-browse-books"
                className="inline-flex items-center gap-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-7 py-4 text-sm font-medium tracking-wide transition-colors"
              >
                Browse the library
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative" data-testid="hero-trailer">
            <div className="relative aspect-video bg-black overflow-hidden">
              <iframe
                title="South on Leadership — Trailer"
                src="https://www.youtube.com/embed/0tQwsSac9D8"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="hidden lg:block absolute -bottom-10 -left-12 w-56 bg-white p-6 border border-brand-line shadow-sm">
              <Quote className="w-5 h-5 text-brand-rust" />
              <p className="font-serif text-base mt-3 leading-snug text-brand-ink">
                The standard you walk past is the standard you accept.
              </p>
              <p className="overline text-brand-inkMuted mt-3">— David Hurley</p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-b border-brand-line">
        <div className="container-narrow grid grid-cols-2 md:grid-cols-4">
          {PILLARS.map((p, i) => (
            <div
              key={p.id}
              data-testid={`pillar-${p.id}`}
              className={`py-8 md:py-12 ${i < PILLARS.length - 1 ? "border-r border-brand-line" : ""} text-center md:text-left md:px-10`}
            >
              <span className="overline text-brand-inkMuted">0{i + 1}</span>
              <p className="font-serif text-2xl md:text-3xl mt-2 text-brand-ink">{p.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FREE GUIDE — top-of-funnel lead magnet */}
      <FreeGuideStrip />

      {/* BUILDING IN PUBLIC — recording progress */}
      {flagshipModules && (
        <CourseProgressStrip
          modules={flagshipModules}
          latestModuleNumber={flagshipProduct?.latest_module_number}
          latestModuleRecordedAt={flagshipProduct?.latest_module_recorded_at}
        />
      )}

      {/* FEATURED BOOKS */}
      <section data-testid="featured-books-section" className="py-20 md:py-28">
        <div className="container-narrow">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="overline text-brand-rust">The Library</span>
              <h2 className="font-display text-4xl md:text-5xl mt-3 text-brand-ink">Featured books</h2>
            </div>
            <Link to="/books" className="overline text-brand-ink hover:text-brand-rust" data-testid="view-all-books">View all →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {books.map((b, i) => <ProductCard key={b.product_id} product={b} index={i} />)}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-brand-ink text-white py-20 md:py-28">
        <div className="container-narrow grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <img src={PORTRAIT} alt="Editor" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="md:col-span-7">
            <span className="overline text-white/60">Our Editorial Standard</span>
            <h3 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              We publish slowly, on purpose.
            </h3>
            <p className="mt-6 text-white/75 leading-relaxed max-w-xl">
              Every book and course is held to a single test: would a senior operator pay for the time it took to read it? If the answer is no, we don't ship it. The result is a small, sharp catalogue worth re-reading.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <div><p className="font-serif text-3xl">12</p><p className="overline text-white/55 mt-1">Titles</p></div>
              <div><p className="font-serif text-3xl">3</p><p className="overline text-white/55 mt-1">Authors</p></div>
              <div><p className="font-serif text-3xl">24k</p><p className="overline text-white/55 mt-1">Leaders</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* BUNDLES */}
      {bundles.length > 0 && (
        <section data-testid="featured-bundles-section" className="bg-white border-y border-brand-line py-20 md:py-28">
          <div className="container-narrow">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="overline text-brand-rust">Editor's Picks</span>
                <h2 className="font-display text-4xl md:text-5xl mt-3 text-brand-ink">Bundles, deliberately priced.</h2>
              </div>
              <Link to="/bundles" className="overline text-brand-ink hover:text-brand-rust" data-testid="view-all-bundles">View all →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {bundles.map((b, i) => (
                <Link
                  key={b.bundle_id}
                  to={`/bundles/${b.slug}`}
                  data-testid={`home-bundle-${b.slug}`}
                  className="group bg-brand-bg border border-brand-line hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex flex-col"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="overline text-brand-rust">{b.products.length} items · save ${b.savings.toFixed(0)}</span>
                    <h3 className="font-serif text-xl text-brand-ink mt-3 leading-snug">{b.title}</h3>
                    <div className="mt-auto pt-5 flex items-end justify-between border-t border-brand-line">
                      <span className="font-serif text-2xl text-brand-ink">${b.bundle_price.toFixed(0)}</span>
                      <span className="text-sm text-brand-inkMuted line-through">${b.list_total.toFixed(0)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED COURSES */}
      <section data-testid="featured-courses-section" className="py-20 md:py-28">
        <div className="container-narrow">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="overline text-brand-rust">The Studio</span>
              <h2 className="font-display text-4xl md:text-5xl mt-3 text-brand-ink">Featured courses</h2>
            </div>
            <Link to="/courses" className="overline text-brand-ink hover:text-brand-rust" data-testid="view-all-courses">View all →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((c, i) => <ProductCard key={c.product_id} product={c} index={i} />)}
          </div>
        </div>
      </section>

      {/* PODCAST — Listen on Spotify */}
      <section data-testid="podcast-section" className="bg-brand-bg border-t border-brand-line py-20 md:py-28">
        <div className="container-narrow grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="overline text-brand-rust">The Podcast</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-[1.05]">
              South on Leadership, <em className="italic">in your ears.</em>
            </h2>
            <p className="mt-6 text-brand-inkMuted leading-relaxed">
              Long-form conversations on character, decision-making, and the quiet standards leaders are held to. New episodes drop bi-weekly.
            </p>
            <a
              href="https://open.spotify.com/show/5BIKkX13rvdHq4falhhmRu"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcast-cta"
              className="inline-flex items-center gap-2 mt-8 bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 0a12 12 0 1 0 12 12A12 12 0 0 0 12 0zm5.5 17.32a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 1 1-.33-1.46c4.57-1.04 8.5-.59 11.66 1.34a.75.75 0 0 1 .25 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.99-8.16-2.56-11.99-1.4a.94.94 0 1 1-.55-1.8c4.38-1.33 9.81-.69 13.52 1.6a.94.94 0 0 1 .31 1.29zm.13-3.4c-3.87-2.3-10.27-2.51-13.96-1.39a1.13 1.13 0 1 1-.66-2.16c4.24-1.29 11.3-1.04 15.76 1.6a1.13 1.13 0 0 1-1.15 1.95z"/></svg>
              Listen on Spotify
            </a>
          </div>
          <div className="lg:col-span-7" data-testid="podcast-embed">
            <iframe
              title="South on Leadership Podcast on Spotify"
              src="https://open.spotify.com/embed/show/5BIKkX13rvdHq4falhhmRu?utm_source=generator"
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="border-0"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}


function FreeGuideStrip() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return toast.error("Email is required");
    setLoading(true);
    try {
      await api.post("/free-guide/signup", { email });
      toast.success("Sent. Opening now.");
      navigate("/field-manual");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Try again");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="bg-brand-bg border-y border-brand-line" data-testid="home-free-guide">
      <div className="container-narrow py-16 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 overline text-brand-rust">
            <BookOpen className="w-4 h-4" /> Free field manual
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-4 text-brand-ink leading-tight">
            The leader you become <em className="italic">in the fire.</em>
          </h2>
          <p className="mt-5 text-brand-inkMuted text-lg leading-relaxed max-w-xl">
            A seven-page primer on how leaders are actually formed. Three reveals. Three weekly drills. A thirty-day starter plan. Sent straight to your inbox.
          </p>
        </div>
        <div className="lg:col-span-5">
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="home-free-guide-form">
            <input
              required
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-white border border-brand-line py-4 px-4 text-brand-ink focus:outline-none focus:border-brand-rust"
              data-testid="home-free-guide-email"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="home-free-guide-submit"
              className="bg-brand-ink text-white hover:bg-black px-6 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {loading ? "Sending…" : "Send me the guide"}
            </button>
          </form>
          <p className="mt-3 text-xs text-brand-inkMuted">
            Free. No tricks. Reply &ldquo;stop&rdquo; any time.{" "}
            <Link to="/free-guide" className="text-brand-rust hover:text-brand-ink underline underline-offset-4">
              See what&apos;s inside &rarr;
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}

// A single YouTube ID substring that marks a placeholder video in the seed
// data. Any real lesson video is a proper YouTube embed and will not contain
// this fragment. Keep this in sync with backend/seed_data.py (_PLACEHOLDER_VIDEO_FRAGMENT).
const PLACEHOLDER_FRAGMENT = "dQw4w9WgXcQ";

function CourseProgressStrip({ modules, latestModuleNumber, latestModuleRecordedAt }) {
  const total = modules.length;
  const recordedCount = modules.filter((m) =>
    (m.lessons || []).every((l) => {
      const u = l.video_url || "";
      return u && !u.includes(PLACEHOLDER_FRAGMENT);
    })
  ).length;
  const pct = Math.round((recordedCount / total) * 100);

  // "🔥 Just dropped" flag — shows for 14 days after the newest module was
  // completed. `latestModuleRecordedAt` is bumped in the backend seed only
  // when the highest-recorded-module number rises, so restarts don't reset
  // the badge and the countdown is stable.
  const daysSinceLatest = latestModuleRecordedAt
    ? (Date.now() - new Date(latestModuleRecordedAt).getTime()) / (1000 * 60 * 60 * 24)
    : Infinity;
  const showJustDropped = latestModuleNumber && daysSinceLatest >= 0 && daysSinceLatest <= 14;
  const latestModule = showJustDropped
    ? modules[Math.min(latestModuleNumber - 1, modules.length - 1)]
    : null;

  return (
    <section className="border-t border-brand-line py-16 md:py-20 bg-white" data-testid="home-course-progress">
      <div className="container-narrow">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <span className="overline text-brand-rust">Building in public</span>
            <h2 className="font-display text-3xl md:text-5xl mt-3 text-brand-ink leading-tight">
              <span data-testid="progress-recorded-count">{recordedCount}</span> of {total} modules recorded.<br />
              <em className="italic">New lessons every week.</em>
            </h2>

            {showJustDropped && (
              <div
                data-testid="just-dropped-flag"
                className="mt-6 inline-flex items-center gap-2 bg-brand-rust text-white pl-3 pr-4 py-2 text-xs uppercase tracking-[.16em] font-medium"
              >
                <Flame className="w-4 h-4" />
                <span data-testid="just-dropped-label">
                  New: Module {latestModuleNumber}{latestModule?.title ? ` — ${latestModule.title.replace(/^Module \d+ — /, "")}` : ""} just dropped
                </span>
              </div>
            )}

            <p className="mt-5 text-brand-inkMuted leading-relaxed">
              Every module of <em>Forged in Fire</em> is being shot in the same studio, in real time. Enroll today and every future lesson unlocks automatically as it&apos;s posted — no upgrade, no extra charge, ever.
            </p>
          </div>

          <Link
            to="/p/prd_b8dca9fc6cf1"
            data-testid="progress-view-curriculum"
            className="border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors inline-flex items-center gap-2 whitespace-nowrap"
          >
            View the curriculum <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Progress bar */}
        <div className="mt-10">
          <div className="h-2 bg-brand-bg border border-brand-line overflow-hidden">
            <div
              className="h-full bg-brand-rust transition-all duration-700"
              style={{ width: `${pct}%` }}
              data-testid="progress-bar"
            />
          </div>
          <p className="mt-2 overline text-brand-inkMuted">
            <span data-testid="progress-percent">{pct}%</span> of the course recorded &middot; {total - recordedCount} module{total - recordedCount === 1 ? "" : "s"} in the queue
          </p>
        </div>

        {/* Per-module chips */}
        <div className="mt-10 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
          {modules.map((m, i) => {
            const recorded = (m.lessons || []).every((l) => {
              const u = l.video_url || "";
              return u && !u.includes(PLACEHOLDER_FRAGMENT);
            });
            return (
              <div
                key={i}
                title={m.title}
                data-testid={`progress-chip-${i + 1}`}
                className={
                  "px-2 py-3 text-center text-xs font-medium border transition-colors " +
                  (recorded
                    ? "bg-brand-ink text-white border-brand-ink"
                    : "bg-brand-bg text-brand-inkMuted border-brand-line")
                }
              >
                <div className="font-display text-lg leading-none">{i + 1}</div>
                <div className="mt-1 overline text-[9px]">
                  {recorded ? "Done" : "Soon"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

